import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Order, OrderProcessingStep } from '@app/core/api-client/models';
import { getOrderDescription } from '@app/core/helpers/order-helper';
import { WhiteMarkService } from '@app/core/layout/white-mark.service';
import { NavigationService, RestaurantSource } from '@app/core/services/navigation.service';
import { OrderService } from '@app/core/services/order/order.service';
import { PageVisibilityService } from '@app/core/ui/page-visibility/page-visibility.service';
import { BehaviorSubject, forkJoin, interval, merge } from 'rxjs';
import { filter, finalize, first, map, tap, throttleTime } from 'rxjs/operators';

function mergeOrders(orders: Order[], newOrders: Order[]): Order[] {
  const ordersMap = new Map<number, Order>();
  (orders || []).forEach(order => ordersMap.set(order.orderId!, order));
  (newOrders || []).forEach(order => ordersMap.set(order.orderId!, order));
  return Array.from(ordersMap.values());
}
@Component({
    templateUrl: './command-list-page.component.html',
    styleUrls: ['./command-list-page.component.scss'],
    standalone: false
})
export class CommandListPageComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  orders: Order[];
  private _ordersCount: number;
  limit: number = 10;
  offset: number = 0;
  displayMoreOrders: boolean = false;
  isLoading: boolean = false;
  isRefreshing: boolean = false;
  isEmbedded$ = this._whiteMark.isEmbedded$();

  private _preparationOrdersSubject = new BehaviorSubject<Order[]>([]);
  preparationOrders$ = this._preparationOrdersSubject.asObservable();
  hasPreparationOrders$ = this.preparationOrders$.pipe(map(orders => orders.length > 0));
  lastPreparationOrder$ = this.preparationOrders$.pipe(map(orders => orders[0]));

  private _suspendOrdersRefresh: boolean = false;

  constructor(
    private _orderService: OrderService,
    private _whiteMark: WhiteMarkService,
    private _cd: ChangeDetectorRef,
    private _navigationService: NavigationService,
    private _pageVisibilityService: PageVisibilityService,
  ) { }

  ngOnInit(): void {
    this.loadInitialData();
    this.monitorAndRefreshLastOrders();
  }

  private monitorAndRefreshLastOrders() {
    // Monitor to refresh LastOrderWithPendingPreparation$ and last non finalized orders of the day
    // do it every 30s or when page becomes visible / app is resumed, if a timer (30s) is due

    merge(
      interval(30000).pipe(
        map(() => 'Timer'),
        filter(() => this._pageVisibilityService.isPageVisible())
      ),
      this._pageVisibilityService.$onPageVisible.pipe(
        map(() => 'PageVisible'),
      )
    ).pipe(
      takeUntilDestroyed(this._destroyRef),
      throttleTime(30_000), // 30s
      filter(() => !this._suspendOrdersRefresh),
      tap((source) => {
        console.log(`CommandListPageComponent: Refreshing orders triggered by ${source}`);
        this.refreshLastOrders();
      })
    )
      .subscribe();

  }

  private refreshLastOrders() {
    //prevent multiple simultaneous refreshes
    if (this.isRefreshing) return;

    this.isRefreshing = true;
    this._cd.markForCheck();

    this._orderService.getLastOrderWithPendingPreparation$().pipe(
      takeUntilDestroyed(this._destroyRef),
      tap(order => {
        this._preparationOrdersSubject.next(order ? [order] : []);
      })
    ).subscribe();

    // //load only last 5 last orders to merge with existing ones
    // this._orderService.getOrders(5, 0).pipe(

    // track only orders that should be tracked
    const ordersToTrack = this.orders.filter(order => this._orderService.orderPreparationShouldBeTracked(order));
    if (ordersToTrack.length === 0) {
      this.isRefreshing = false;
      this._cd.markForCheck();
      return;
    }

    forkJoin(
      ordersToTrack.map(order => this._orderService.getOrder(order.orderId))
    ).pipe(
      takeUntilDestroyed(this._destroyRef),
      first(),
      tap((newOrders) => {
        this.orders = mergeOrders(this.orders, newOrders);
      }),
      finalize(() => {
        this.isRefreshing = false;
        this._cd.markForCheck();
      })
    ).subscribe();
  }


  private loadInitialData() {
    this.isLoading = true;
    this._cd.markForCheck();

    this._orderService.getLastOrderWithPendingPreparation$().pipe(
      takeUntilDestroyed(this._destroyRef),
      tap(order => {
        this._preparationOrdersSubject.next(order ? [order] : []);
      })
    ).subscribe();

    forkJoin([
      this._orderService.getOrdersCount(),
      this._orderService.getOrders(this.limit, this.offset),
    ])
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(([count, orders]) => {
          this._ordersCount = count;
          this.orders = mergeOrders(this.orders, orders);
          this.displayMoreOrders = this.orders.length < this._ordersCount;
          // this.computePreparationOrders();
        }),
        finalize(() => {
          this.isLoading = false;
          this._cd.markForCheck();
        })
      )
      .subscribe();
  }

  onRefresh() {
    this.refreshLastOrders();
  }

  goToOrderDetail(order: Order) {

    this._suspendOrdersRefresh = true;
    const bs = this._orderService.displayOrderBottomSheet(order);
    bs?.afterDismissed().pipe(
      takeUntilDestroyed(this._destroyRef),
      first(),
      tap(() => {
        this._suspendOrdersRefresh = false;
        this.refreshLastOrders();
      })
    ).subscribe();

  }

  goToCreateOrder() {
    this._navigationService.navigateToDefaultMenu$(RestaurantSource.UserRestaurant).subscribe();
  }

  goToTrackOrder(order: Order) {
    // this._navigationService.navigateToOrderTrackingPage(order);
    // this._orderService.processOrderActions(order, false).subscribe();
    this._orderService.displayOrderTableServiceDialog(order);
  }

  getMoreOrders() {
    this.isLoading = true;
    const newOffset = this.offset + this.limit;
    this._orderService
      .getOrders(this.limit, newOffset)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((newOrders) => {
          this.offset = newOffset;
          this.orders = mergeOrders(this.orders, newOrders);
          this.displayMoreOrders = this.orders.length < this._ordersCount;
          // this.computePreparationOrders();
        }),
        finalize(() => {
          this.isLoading = false;
          this._cd.markForCheck();
        })
      )
      .subscribe();
  }



  // private computePreparationOrders() {
  //   const preparationOrders = this.orders.filter(order => order.processingState.isDailyPreparationOrder);

  //   // sort by processingStep priority
  //   preparationOrders.sort((a, b) => {
  //     return (OrderProcessingStepPriority[a.processingState?.step] ?? 99) - (OrderProcessingStepPriority[b.processingState?.step] ?? 99);
  //   });

  //   this._preparationOrdersSubject.next(preparationOrders);
  // }

  getOrderDescription(order: Order | null) {
    return getOrderDescription(order);
  }

}

const OrderProcessingStepPriority: { [key in OrderProcessingStep]?: number } =
{
  [OrderProcessingStep.Creating]: 1,
  [OrderProcessingStep.PendingValidation]: 2,
  [OrderProcessingStep.PendingPreparation]: 3,
  [OrderProcessingStep.Processing]: 4,
  [OrderProcessingStep.Processed]: 5,
  [OrderProcessingStep.Finalized]: 6,
  [OrderProcessingStep.Cancelled]: 7
};
